import React, { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Mail,
  MapPin,
  Phone,
  Scale,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "#2B0000";
const MAROON_DARK = "#190000";
const GOLD = "#D6B33F";
const GOLD_LIGHT = "#E9CD7A";
const CREAM = "#FBF6EF";
const SOFT_TEXT = "#6F625E";

const sections = [
  {
    number: "01",
    title: "Use of Website",
    points: [
      "You must be at least 18 years old to use our Website.",
      "You agree not to misuse our Website, including attempting unauthorized access or interfering with its functionality.",
      "All content (images, logos, product descriptions) is owned by SL Gold Kanthi and may not be used without permission.",
    ],
  },
  {
    number: "02",
    title: "Products and Pricing",
    points: [
      "We strive to display accurate product descriptions, images, and prices. However, errors may occur, and we reserve the right to correct them.",
      "Prices are subject to change without prior notice.",
      "All prices are displayed in INR unless stated otherwise.",
    ],
  },
  {
    number: "03",
    title: "Orders and Payments",
    points: [
      "By placing an order, you agree to provide accurate and complete information.",
      "Orders are confirmed only after successful payment.",
      "We use secure payment gateways; however, we are not responsible for errors or issues caused by third-party payment providers.",
    ],
  },
  {
    number: "04",
    title: "Shipping and Delivery",
    points: [
      "Delivery timelines are estimates and may vary due to external factors.",
      "We are not liable for delays caused by courier services or unforeseen events.",
      "Shipping charges, if any, will be displayed at checkout.",
    ],
  },
  {
    number: "05",
    title: "Returns, Refunds & Exchanges",
    points: [
      "Returns and exchanges are subject to our Return Policy.",
      "Customized or personalized products may not be eligible for returns unless defective.",
      "Refunds (if applicable) will be processed within 7-10 business days.",
    ],
  },
  {
    number: "06",
    title: "Limitation of Liability",
    points: [
      "SL Gold Kanthi is not responsible for indirect, incidental, or consequential damages arising from your use of the Website or products.",
      "Our maximum liability is limited to the amount paid for the product in question.",
    ],
  },
  {
    number: "07",
    title: "Intellectual Property",
    points: [
      "All trademarks, logos, and content on this Website are the property of SL Gold Kanthi.",
      "Unauthorized use of our intellectual property is strictly prohibited.",
    ],
  },
  {
    number: "08",
    title: "Privacy Policy",
    points: [
      "Your use of our Website is also governed by our Privacy Policy.",
    ],
  },
  {
    number: "09",
    title: "Governing Law",
    points: [
      "These Terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of the courts in Delhi, India.",
    ],
  },
  {
    number: "10",
    title: "Changes to Terms",
    points: [
      "We may update these Terms from time to time. Continued use of our Website implies acceptance of the updated Terms.",
    ],
  },
];

export default function TermsAndConditions() {
  const { theme } = useTheme();

  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const headingRef = useRef(null);
  const sectionsRef = useRef(null);
  const contactRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            heroRef.current,
            headingRef.current,
            sectionsRef.current,
            contactRef.current,
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
          }
        );
        return;
      }

      // Hero animation
      gsap.fromTo(
        heroRef.current,
        {
          opacity: 0,
          y: 35,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.05,
          ease: "power3.out",
        }
      );

      // Heading animation
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          x: -45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          delay: 0.15,
          ease: "power3.out",
        }
      );

      // Sections animation
      const sectionElements = gsap.utils.toArray(".terms-section");
      gsap.fromTo(
        sectionElements,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Point items animation
      const pointItems = gsap.utils.toArray(".terms-point");
      gsap.fromTo(
        pointItems,
        {
          opacity: 0,
          x: 20,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Contact section animation
      gsap.fromTo(
        contactRef.current,
        {
          opacity: 0,
          y: 40,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contactRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Parallax effect for hero image
      gsap.to(".terms-hero-image", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const bg = theme?.user_bg || CREAM;
  const maroon = theme?.user_maroon || MAROON;
  const gold = theme?.user_gold || GOLD;
  const text = theme?.user_text || MAROON_DARK;
  const textSoft = theme?.user_text_soft || SOFT_TEXT;
  const card = theme?.user_card || "#FFF9F6";
  const border = theme?.user_border || "rgba(43,0,0,0.12)";

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden"
      style={{ backgroundColor: bg, color: text }}
    >
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="
          relative min-h-[380px] overflow-hidden
          border-b border-[#D6B33F]/20
          sm:min-h-[400px]
          lg:min-h-[320px]
        "
      >
        <div className="absolute inset-0">
          <img
            className="
              terms-hero-image h-full w-full scale-[1.08] object-cover
              object-[55%_center]
              lg:scale-105 lg:object-center
            "
            src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=90&w=1800&auto=format&fit=crop"
            alt="Terms and conditions"
          />
          <div
            className="
              absolute inset-0
              bg-gradient-to-b
              from-[#FBF6EF]/95 via-[#FBF6EF]/65 to-[#FBF6EF]/95
              lg:bg-gradient-to-r
              lg:from-[#FBF6EF] lg:via-[#FBF6EF]/40 lg:to-transparent
            "
          />
          <div className="absolute inset-0 bg-[#2B0000]/10" />
        </div>

        <div
          className="
            relative z-10 mx-auto flex min-h-[380px] max-w-[1420px]
            items-center px-4 pb-8 pt-12
            sm:min-h-[400px] sm:px-6 sm:pt-14
            lg:min-h-[320px] lg:px-14 lg:py-14
            xl:px-20
          "
        >
          <div
            ref={headingRef}
            className="
              w-full max-w-[700px] rounded-[24px]
              border border-white/60 bg-[#FBF6EF]/80
              px-4 py-5 shadow-[0_24px_70px_rgba(43,0,0,0.08)]
              backdrop-blur-[5px]
              sm:px-6 sm:py-7
              lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none
              lg:backdrop-blur-none
            "
          >
            <Link
              to="/"
              className="
                inline-flex items-center gap-2 text-xs font-semibold
                uppercase tracking-[0.14em] transition-all duration-300
                hover:gap-3 hover:opacity-70
              "
              style={{ color: MAROON }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>

            <div className="mt-4 flex items-center gap-3">
              <span
                className="h-px w-12"
                style={{ backgroundColor: GOLD }}
              />
              <Sparkles className="h-5 w-5" style={{ color: GOLD }} />
              <span
                className="h-px w-12"
                style={{ backgroundColor: GOLD }}
              />
            </div>

            <p
              className="mt-4 text-[10px] font-bold uppercase tracking-[0.28em] sm:text-[11px]"
              style={{ color: GOLD }}
            >
              Legal Information
            </p>

            <h1
              className="
                mt-2 font-serif text-[32px] leading-[1.05]
                sm:text-[44px] lg:text-[58px]
              "
              style={{ color: MAROON_DARK }}
            >
              Terms and Conditions
            </h1>

            <p
              className="mt-2 font-serif text-lg sm:text-xl"
              style={{ color: MAROON }}
            >
              SL Gold Kanthi
            </p>

            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs sm:px-4 sm:py-2"
              style={{
                borderColor: border,
                backgroundColor: "rgba(255,255,255,0.42)",
                color: textSoft,
              }}
            >
              <CalendarDays className="h-4 w-4" style={{ color: GOLD }} />
              Effective Date: July 2026
            </div>

            <p
              className="mt-4 text-sm leading-6 sm:text-[15px] sm:leading-7"
              style={{ color: textSoft }}
            >
              Welcome to SL Gold Kanthi. These Terms and Conditions govern your
              use of our website and the purchase of products from us. By
              accessing our Website, you agree to comply with these Terms.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="relative px-4 py-10 sm:px-6 lg:px-14 lg:py-16 xl:px-20">
        <div className="pointer-events-none absolute left-0 top-0 h-72 w-72 rounded-full bg-[#D6B33F]/[0.05] blur-3xl" />

        <div
          className="relative mx-auto max-w-[1420px]"
          ref={sectionsRef}
        >
          <div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
            {sections.map((section) => (
              <article
                key={section.number}
                className="
                  terms-section rounded-[20px] border
                  bg-white/55 p-5 shadow-[0_12px_35px_rgba(43,0,0,0.04)]
                  backdrop-blur-sm transition-all duration-300
                  hover:shadow-[0_18px_45px_rgba(43,0,0,0.08)]
                  sm:p-6
                  lg:p-7
                "
                style={{
                  backgroundColor: card,
                  borderColor: border,
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="
                      grid h-11 w-11 shrink-0 place-items-center
                      rounded-full font-serif text-sm font-bold
                      sm:h-12 sm:w-12 sm:text-base
                    "
                    style={{
                      backgroundColor: MAROON,
                      color: GOLD_LIGHT,
                    }}
                  >
                    {section.number}
                  </div>

                  <div className="min-w-0">
                    <h2
                      className="font-serif text-xl font-semibold leading-tight sm:text-2xl lg:text-[26px]"
                      style={{ color: MAROON_DARK }}
                    >
                      {section.title}
                    </h2>

                    <div className="mt-3 space-y-2.5 sm:space-y-3">
                      {section.points.map((point, index) => (
                        <div
                          key={`${section.number}-${index}`}
                          className="terms-point flex items-start gap-3"
                        >
                          <CheckCircle2
                            className="mt-1 h-4 w-4 shrink-0"
                            style={{ color: GOLD }}
                          />
                          <p
                            className="text-sm leading-6 sm:text-[15px]"
                            style={{ color: textSoft }}
                          >
                            {point}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        ref={contactRef}
        className="relative px-4 pb-12 sm:px-6 lg:px-14 lg:pb-16 xl:px-20"
      >
        <div className="mx-auto max-w-[1420px]">
          <div
            className="
              overflow-hidden rounded-[24px] border
              shadow-[0_20px_55px_rgba(43,0,0,0.08)]
            "
            style={{
              backgroundColor: MAROON_DARK,
              borderColor: "rgba(212,175,55,0.35)",
            }}
          >
            <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
              {/* Left Column */}
              <div className="p-6 sm:p-8 lg:p-10">
                <div className="flex items-center gap-3">
                  <Scale className="h-6 w-6" style={{ color: GOLD }} />
                  <span
                    className="text-xs font-bold uppercase tracking-[0.24em]"
                    style={{ color: GOLD }}
                  >
                    11. Contact Us
                  </span>
                </div>

                <h2
                  className="mt-4 font-serif text-2xl font-medium sm:text-3xl lg:text-4xl"
                  style={{ color: "#FFF7EB" }}
                >
                  Questions about these Terms?
                </h2>

                <p
                  className="mt-4 max-w-xl text-sm leading-7 sm:text-[15px]"
                  style={{ color: "rgba(255,247,235,0.78)" }}
                >
                  For any questions or concerns regarding these Terms, please
                  contact us:
                </p>

                <div className="mt-6">
                  <p
                    className="font-serif text-2xl font-semibold"
                    style={{ color: GOLD_LIGHT }}
                  >
                    SL Gold Kanthi
                  </p>
                </div>
              </div>

              {/* Right Column - Contact Details */}
              <div
                className="grid gap-4 border-t p-6 sm:p-8 lg:border-l lg:border-t-0 lg:p-10"
                style={{ borderColor: "rgba(212,175,55,0.22)" }}
              >
                <a
                  href="mailto:slgoldkanthi@info.com"
                  className="
                    group flex items-start gap-4 rounded-2xl border p-4
                    transition-all duration-300 hover:-translate-y-1
                    hover:shadow-[0_12px_30px_rgba(0,0,0,0.2)]
                  "
                  style={{
                    borderColor: "rgba(212,175,55,0.22)",
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Mail
                    className="mt-0.5 h-5 w-5 shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ color: GOLD }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.18em]" style={{ color: GOLD }}>
                      Email
                    </p>
                    <p className="mt-1 text-sm break-words sm:text-base" style={{ color: "#FFF7EB" }}>
                      slgoldkanthi@info.com
                    </p>
                  </div>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1"
                    style={{ color: GOLD }}
                  />
                </a>

                <a
                  href="tel:+918640051400"
                  className="
                    group flex items-start gap-4 rounded-2xl border p-4
                    transition-all duration-300 hover:-translate-y-1
                    hover:shadow-[0_12px_30px_rgba(0,0,0,0.2)]
                  "
                  style={{
                    borderColor: "rgba(212,175,55,0.22)",
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Phone
                    className="mt-0.5 h-5 w-5 shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ color: GOLD }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.18em]" style={{ color: GOLD }}>
                      Phone
                    </p>
                    <p className="mt-1 text-sm sm:text-base" style={{ color: "#FFF7EB" }}>
                      +91-86400-51400
                    </p>
                  </div>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1"
                    style={{ color: GOLD }}
                  />
                </a>

                <div
                  className="
                    flex items-start gap-4 rounded-2xl border p-4
                    transition-all duration-300 hover:-translate-y-1
                    hover:shadow-[0_12px_30px_rgba(0,0,0,0.2)]
                  "
                  style={{
                    borderColor: "rgba(212,175,55,0.22)",
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <MapPin
                    className="mt-0.5 h-5 w-5 shrink-0"
                    style={{ color: GOLD }}
                  />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em]" style={{ color: GOLD }}>
                      Address
                    </p>
                    <p className="mt-1 text-sm leading-6 sm:text-base" style={{ color: "#FFF7EB" }}>
                      Building No.1149, Shop No.5, 1st floor, Kucha Mahajani,
                      Chandani Chowk, Delhi, India 110006
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="px-4 pb-8 sm:px-6 lg:px-14 lg:pb-10 xl:px-20">
        <div className="mx-auto max-w-[1420px] text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-16" style={{ backgroundColor: GOLD }} />
            <ShieldCheck className="h-5 w-5" style={{ color: GOLD }} />
            <span className="h-px w-16" style={{ backgroundColor: GOLD }} />
          </div>

          <p
            className="mt-4 text-xs uppercase tracking-[0.18em]"
            style={{ color: textSoft }}
          >
            SL Gold Kanthi • Terms and Conditions
          </p>
        </div>
      </section>
    </main>
  );
}